import { Skeleton, SkeletonText } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface SimpleTableSkeletonProps {
    readonly rows?: number;
    /** Header labels' pixel widths, left to right. Length sets the column count. */
    readonly headerWidths?: readonly number[];
    /**
     * Column indexes rendered as right-aligned figures. Defaults to everything
     * after the first, matching the metric tables this stands in for.
     */
    readonly numericFrom?: number;
    readonly className?: string;
}

/** First-column name widths, staggered so rows don't align into a block. */
const LABEL_WIDTHS = ['54%', '38%', '66%', '46%', '58%', '42%'] as const;

/**
 * Loading placeholder for a hand-rolled `<table>` — the metric tables that are
 * not `DataTable`, such as the workflow step-performance funnel.
 *
 * Reproduces that markup's own metrics rather than `DataTable`'s: a
 * `bg-bg-table-header` head row with `py-2` and body rows at `py-2.5`, both
 * separated by the same bottom borders.
 */
export function SimpleTableSkeleton({
    rows = 6,
    headerWidths = [40, 56, 62, 44, 52],
    numericFrom = 1,
    className,
}: SimpleTableSkeletonProps) {
    return (
        <div className={cn('overflow-x-auto', className)}>
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-border-default bg-bg-table-header text-left">
                        {headerWidths.map((width, i) => (
                            <th
                                key={i}
                                className={cn(
                                    'py-2',
                                    i === 0 ? 'pr-4' : 'px-4',
                                    i === headerWidths.length - 1 && 'pr-0 pl-4',
                                )}
                            >
                                <Skeleton
                                    className={cn('h-2 rounded-sm', i >= numericFrom && 'ml-auto')}
                                    style={{ width }}
                                />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Array.from({ length: rows }).map((_, rowIndex) => (
                        <tr key={rowIndex} className="border-b border-border-default last:border-0">
                            {headerWidths.map((_width, colIndex) => (
                                <td
                                    key={colIndex}
                                    className={cn(
                                        'py-2.5',
                                        colIndex === 0 ? 'pr-4' : 'px-4',
                                        colIndex === headerWidths.length - 1 && 'pr-0 pl-4',
                                    )}
                                >
                                    {colIndex < numericFrom ? (
                                        <SkeletonText
                                            size="sm"
                                            width={LABEL_WIDTHS[rowIndex % LABEL_WIDTHS.length]}
                                        />
                                    ) : (
                                        <SkeletonText
                                            size="sm"
                                            width="28px"
                                            className="justify-end"
                                        />
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
